import React from 'react'
import { Link } from 'react-router-dom'
import { Badge, Button } from 'reactstrap'
import moment from 'moment'

import { isPreviewable, REPORT_TYPES } from './constants'

const DAY_MS = 24 * 60 * 60 * 1000

export const reportAgeDays = (reportDate) =>
  Math.max(0, Math.floor((Date.now() - new Date(reportDate).getTime()) / DAY_MS))

export const reportAgeLabel = (days) => {
  if (days < 1) return 'today'
  if (days < 30) return `${days}d old`
  if (days < 365) return `${Math.floor(days / 30)}mo old`
  return `${Math.floor(days / 365)}y old`
}

// Freshness is the whole point of the library: a recent report can be reused, an old
// one probably has to be re-pulled anyway.
const ageColor = (days) => (days <= 30 ? 'success' : days <= 90 ? 'warning' : 'secondary')

export const ReportAgeBadge = ({ reportDate }) => {
  const days = reportAgeDays(reportDate)
  return (
    <Badge color={ageColor(days)} className='ml-1'>
      {reportAgeLabel(days)}
    </Badge>
  )
}

const sharedWithSummary = (row) => {
  const parts = []
  if (row.divisionNames?.length) parts.push(row.divisionNames.join(', '))
  if (row.roleNames?.length) parts.push(`Roles: ${row.roleNames.join(', ')}`)
  if (row.userNames?.length) parts.push(`Users: ${row.userNames.join(', ')}`)
  return parts.length ? parts.join(' | ') : 'Everyone'
}

const createSharedResourcesColumns = ({ onPreview, onDownload, onEdit, onDelete, canDownload, canEdit, canDelete }) => [
  {
    field: 'title',
    header: 'Title',
    sortable: true,
    body: (row) => (
      <div>
        <strong>{row.title}</strong>
        <br />
        <small className='text-muted'>{row.file?.name}</small>
      </div>
    ),
  },
  {
    field: 'reportTypes',
    header: 'Types',
    body: (row) => (row.reportTypes || []).map((type) => REPORT_TYPES[type] || type).join(', '),
  },
  {
    field: 'reportDate',
    header: 'Report Date',
    sortable: true,
    body: (row) => (
      <span>
        {moment(row.reportDate).format('MM/DD/YYYY')}
        <ReportAgeBadge reportDate={row.reportDate} />
      </span>
    ),
  },
  {
    field: 'contactNames',
    header: 'Contacts',
    body: (row) =>
      (row.contactIds || []).length
        ? (row.contactIds || []).map((id, index) => (
            <span key={id}>
              {index > 0 && ', '}
              <Link to={`/contacts/${id}`}>{row.contactNames?.[index] || `#${id}`}</Link>
            </span>
          ))
        : '—',
  },
  {
    field: 'sharedWith',
    header: 'Shared With',
    body: (row) => <small>{sharedWithSummary(row)}</small>,
  },
  {
    field: 'createdByObj',
    header: 'Uploaded By',
    body: (row) => (
      <span>
        {row.createdByObj?.name}
        <br />
        <small className='text-muted'>{moment(row.creationDate).format('MM/DD/YYYY')}</small>
      </span>
    ),
  },
  {
    field: 'rowActions',
    header: '',
    style: { width: '160px' },
    body: (row) => (
      <div className='d-flex'>
        {isPreviewable(row.file?.type) ? (
          <Button size='sm' color='primary' className='mr-1' onClick={() => onPreview(row)} title='View'>
            <i className='fa fa-eye' />
          </Button>
        ) : (
          // No inline viewer for this type (e.g. Word). Greyed, disabled; the span
          // carries the tooltip because disabled buttons don't fire hover events.
          <span title='Preview not available for this file type — download to open' className='mr-1 d-inline-block'>
            <Button size='sm' color='secondary' outline disabled style={{ cursor: 'not-allowed' }}>
              <i className='fa fa-eye-slash' />
            </Button>
          </span>
        )}
        {canDownload && (
          <Button size='sm' color='info' className='mr-1' onClick={() => onDownload(row)} title='Download'>
            <i className='fa fa-download' />
          </Button>
        )}
        {canEdit(row) && (
          <Button size='sm' color='secondary' className='mr-1' onClick={() => onEdit(row)} title='Edit'>
            <i className='fa fa-pencil' />
          </Button>
        )}
        {canDelete(row) && (
          <Button size='sm' color='danger' onClick={() => onDelete(row)} title='Delete'>
            <i className='fa fa-trash' />
          </Button>
        )}
      </div>
    ),
  },
]

export default createSharedResourcesColumns
